/**
 * `/connections` — SMB Connections Hub (P2.5-A).
 *
 * Replaces the former MCP-centric connections page. Organized by what each
 * connector does for the business, not by underlying technology. MCP raw
 * instance/server management is removed from this view; it moves to an
 * internal ops page in Phase 4/5.
 *
 * Sections:
 *   Lead Channels   — Twilio SMS/Voice, Gmail inbox, Web form webhook
 *   Social Publishing — Meta/LinkedIn/Twitter OAuth
 *   Scheduling      — Booking link (Calendly/Cal.com/custom)
 *   Coming Soon     — Slack, Google Business, Stripe
 */

import type {
  EmailConnectionResponse,
  EmailMatchField,
  SocialIntegrationStatus,
} from "@simpliwork/api-schema";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/toast";
import {
  useBookingLink,
  useConnectEmailInbox,
  useConnectIntegration,
  useDisconnectEmailInbox,
  useDisconnectIntegration,
  useEmailConnection,
  useIntegrations,
  useUpdateBookingLink,
  useUpdateEmailConnection,
  useTwilioSettings,
  useUpdateTwilioSettings,
  settingsKeys,
} from "@/hooks/use-settings";
import { resolveApiBaseUrl } from "@/lib/api-client";
import { formatTimestamp } from "@/lib/formatters";

const API_BASE = resolveApiBaseUrl({
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  DEV: import.meta.env.DEV,
});

// ─── Shared primitives ────────────────────────────────────────────────────────

/**
 * Open an OAuth/connect flow in a popup window so the Relay app stays open in the
 * current tab (issue #118). Falls back to same-tab navigation if the popup is
 * blocked. Invokes `onClose` once the popup is dismissed (the provider redirects
 * back to /connections, which auto-closes the popup) so the caller can refetch
 * connection status — refetchOnWindowFocus is disabled globally, so we refresh
 * explicitly.
 */
function openOAuthPopup(authUrl: string, onClose: () => void): void {
  const popup = window.open(authUrl, "relay-oauth", "width=600,height=750");
  if (!popup) {
    // Popup blocked — preserve the old behavior so the user can still connect.
    window.location.href = authUrl;
    return;
  }
  const timer = window.setInterval(() => {
    if (popup.closed) {
      window.clearInterval(timer);
      onClose();
    }
  }, 800);
}

function StatusBadge({
  connected,
  label,
  tone,
}: {
  connected: boolean;
  label?: string;
  tone?: "amber";
}) {
  const toneClass =
    tone === "amber"
      ? "bg-amber-100 text-amber-800"
      : connected
        ? "bg-green-100 text-green-800"
        : "bg-gray-100 text-gray-500";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${toneClass}`}
    >
      {label ?? (connected ? "Connected" : "Not connected")}
    </span>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={copy} className="shrink-0">
      {copied ? "Copied!" : "Copy"}
    </Button>
  );
}

function ConnectorRow({ children }: { children: React.ReactNode }) {
  return <div className="px-5 py-4 border-b border-gray-100 last:border-0">{children}</div>;
}

function ConnectorHeader({
  icon,
  label,
  description,
  rightSlot,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  rightSlot?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600 select-none shrink-0">
        {icon}
      </div>
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
      {rightSlot && <div className="flex items-center gap-2 shrink-0">{rightSlot}</div>}
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Card className="border-gray-200 divide-y divide-gray-100">{children}</Card>
    </div>
  );
}

// ─── Twilio ───────────────────────────────────────────────────────────────────

function TwilioConnector() {
  const settings = useTwilioSettings();
  const update = useUpdateTwilioSettings();
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState("");
  const [token, setToken] = useState("");
  const [fromNum, setFromNum] = useState("");

  const configured = settings.data?.configured ?? false;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    update.mutate(
      { account_sid: sid, auth_token: token, from_number: fromNum },
      {
        onSuccess: () => {
          setOpen(false);
          setSid("");
          setToken("");
          setFromNum("");
        },
      },
    );
  }

  return (
    <ConnectorRow>
      <ConnectorHeader
        icon="T"
        label="Twilio (SMS & Voice)"
        description="Receive inbound SMS leads and send instant text responses."
        rightSlot={
          <>
            {settings.isLoading ? (
              <Skeleton className="h-5 w-24 rounded-full" />
            ) : (
              <StatusBadge connected={configured} label={configured ? settings.data?.from_number ?? "Connected" : "Not connected"} />
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? "Cancel" : configured ? "Reconfigure" : "Configure"}
            </Button>
          </>
        }
      />

      {open && (
        <div className="mt-4 flex flex-col gap-5 pl-12">
          {/* Webhook URLs to paste into Twilio console */}
          {settings.data && (
            <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-medium text-gray-700">Paste these into your Twilio phone number settings:</p>
              <div>
                <span className="text-xs text-muted-foreground block mb-1">SMS webhook</span>
                <div className="flex gap-2 items-center">
                  <Input readOnly value={settings.data.webhook_sms_url} className="font-mono text-xs bg-white h-8" />
                  <CopyButton value={settings.data.webhook_sms_url} />
                </div>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block mb-1">Voice webhook</span>
                <div className="flex gap-2 items-center">
                  <Input readOnly value={settings.data.webhook_voice_url} className="font-mono text-xs bg-white h-8" />
                  <CopyButton value={settings.data.webhook_voice_url} />
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="twilio-sid" className="text-xs">Account SID</Label>
                <Input
                  id="twilio-sid"
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={sid}
                  onChange={(e) => setSid(e.target.value)}
                  autoComplete="off"
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="twilio-token" className="text-xs">Auth Token</Label>
                <Input
                  id="twilio-token"
                  type="password"
                  placeholder="••••••••••••••••"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  autoComplete="new-password"
                  className="h-8 text-sm"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="twilio-from" className="text-xs">From Number (E.164)</Label>
              <Input
                id="twilio-from"
                placeholder="+15551234567"
                value={fromNum}
                onChange={(e) => setFromNum(e.target.value)}
                className="h-8 text-sm max-w-xs"
              />
            </div>
            {update.isError && (
              <p className="text-xs text-destructive">
                {update.error instanceof Error ? update.error.message : "Failed to save"}
              </p>
            )}
            {update.isSuccess && <p className="text-xs text-green-700">Saved.</p>}
            <Button type="submit" size="sm" disabled={!sid || !token || !fromNum || update.isPending} className="self-start">
              {update.isPending ? "Saving…" : "Save credentials"}
            </Button>
          </form>
        </div>
      )}
    </ConnectorRow>
  );
}

// ─── Gmail inbox ─────────────────────────────────────────────────────────────

function GmailConnector() {
  const emailConn = useEmailConnection();
  const connectInbox = useConnectEmailInbox();
  const qc = useQueryClient();
  const update = useUpdateEmailConnection();
  const disconnect = useDisconnectEmailInbox();
  const [matchField, setMatchField] = useState<EmailMatchField>("subject");
  const [matchValue, setMatchValue] = useState("");
  const [sendOutbound, setSendOutbound] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);

  const connection: EmailConnectionResponse | null = emailConn.data ?? null;

  useEffect(() => {
    if (connection) {
      setMatchField(connection.match_field);
      setMatchValue(connection.match_value);
      setSendOutbound(connection.send_outbound);
    }
  }, [connection]);

  function getErrorStatus(err: unknown): number | undefined {
    if (err && typeof err === "object" && "status" in err) return (err as { status: number }).status;
    return undefined;
  }

  const hasLoadError = emailConn.isError && getErrorStatus(emailConn.error) !== 404;
  const connectErrorStatus = getErrorStatus(connectInbox.error);
  const connectErrorMsg =
    connectErrorStatus === 503
      ? "Gmail OAuth is not configured. Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to the worker environment."
      : connectInbox.error instanceof Error
        ? connectInbox.error.message
        : "Connection failed.";

  async function handleConnect() {
    try {
      const { auth_url } = await connectInbox.mutateAsync();
      openOAuthPopup(auth_url, () => {
        void qc.invalidateQueries({ queryKey: settingsKeys.emailConnection() });
      });
    } catch {
      // surfaces via connectInbox.isError
    }
  }

  function handleSaveConfig(e: React.FormEvent) {
    e.preventDefault();
    update.mutate(
      {
        match_field: matchField,
        match_value: matchValue,
        send_outbound: sendOutbound,
      },
      { onSuccess: () => setConfigOpen(false) },
    );
  }

  return (
    <ConnectorRow>
      <ConnectorHeader
        icon="@"
        label="Gmail inbox"
        description="Capture inbound emails as leads automatically."
        rightSlot={
          <>
            {emailConn.isLoading ? (
              <Skeleton className="h-5 w-24 rounded-full" />
            ) : (
              <StatusBadge connected={!!connection} />
            )}
            {!emailConn.isLoading && connection && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfigOpen((v) => !v)}
              >
                {configOpen ? "Cancel" : "Configure"}
              </Button>
            )}
            {!emailConn.isLoading && !connection && !hasLoadError && (
              <Button type="button" size="sm" onClick={handleConnect} disabled={connectInbox.isPending}>
                {connectInbox.isPending ? "Redirecting…" : "Connect"}
              </Button>
            )}
          </>
        }
      />

      {connection && (
        <div className="mt-2 pl-12">
          <p className="text-xs text-muted-foreground">
            {connection.email_address}
            {connection.last_polled_at && (
              <> · Last synced {formatTimestamp(connection.last_polled_at)}</>
            )}
          </p>
        </div>
      )}

      {connectInbox.isError && (
        <p className="mt-2 pl-12 text-xs text-destructive">{connectErrorMsg}</p>
      )}

      {connection && configOpen && (
        <form onSubmit={handleSaveConfig} className="mt-4 pl-12 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Match field</Label>
            <div className="flex gap-4">
              {(["subject", "to"] as const).map((f) => (
                <label key={f} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="radio" name="match_field" value={f} checked={matchField === f} onChange={() => setMatchField(f)} />
                  {f === "subject" ? "Subject prefix" : "To address (exact)"}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="match-val" className="text-xs">{matchField === "subject" ? "Subject prefix" : "To email address"}</Label>
            <Input
              id="match-val"
              value={matchValue}
              onChange={(e) => setMatchValue(e.target.value)}
              placeholder={matchField === "subject" ? "Lead:" : "leads@yourdomain.com"}
              className="h-8 text-sm max-w-xs"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sendOutbound}
                onChange={(e) => setSendOutbound(e.target.checked)}
                className="mt-0.5"
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-xs font-medium">Send outbound emails through Gmail</span>
                <span className="text-[0.7rem] text-muted-foreground">
                  When on, replies sent from the lead inbox go through this Gmail account — they
                  show up in your Sent folder and recipient replies route back to your inbox. When
                  off, outbound falls back to Relay's shared sender.
                </span>
              </span>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={!matchValue.trim() || update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
              className="text-destructive hover:text-destructive"
            >
              {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
            </Button>
          </div>
          {update.isError && <p className="text-xs text-destructive">Failed to save settings.</p>}
        </form>
      )}
    </ConnectorRow>
  );
}

// ─── Web form ─────────────────────────────────────────────────────────────────

function WebFormConnector() {
  const webhookUrl = `${API_BASE}/webhooks/lead-form`;
  return (
    <ConnectorRow>
      <ConnectorHeader
        icon="⌘"
        label="Web form"
        description="POST to this URL from your website form to instantly create a lead and trigger SMS response."
        rightSlot={
          <>
            <StatusBadge connected label="Always active" />
            <CopyButton value={webhookUrl} />
          </>
        }
      />
      <div className="mt-2 pl-12">
        <Input readOnly value={webhookUrl} className="font-mono text-xs bg-gray-50 h-8 max-w-md" />
      </div>
    </ConnectorRow>
  );
}

// ─── Social platforms ─────────────────────────────────────────────────────────

// ADR-019 Option 3, step 2: the Instagram-Login product is now configured on the Meta
// app (Instagram App ID + OAuth redirect URI on api.simpliworkai.com), so the standalone
// Instagram tile is live. NOTE: the app is still in dev mode pre-App-Review (ADR-019
// step 4), so only Instagram accounts added as Testers can connect until App Review is
// approved — that's expected on staging.
const INSTAGRAM_TILE_ENABLED = true;

const SOCIAL_META: Record<string, { label: string; icon: string; description: string }> = {
  meta: { label: "Facebook & Instagram", icon: "f", description: "Post to your Facebook Page and its linked Instagram in one connection — best if you manage both. (Needs a Facebook Page.)" },
  instagram: { label: "Instagram", icon: "📷", description: "Connect Instagram on its own — no Facebook Page needed. Best for Instagram-only accounts. Requires a Business or Creator account." },
  linkedin: { label: "LinkedIn", icon: "in", description: "Publishes to your personal LinkedIn profile. Company Page posting isn't available yet." },
  twitter: { label: "Twitter / X", icon: "𝕏", description: "Post tweets from the Content module." },
};

function SocialConnector({ status }: { status: SocialIntegrationStatus }) {
  const connect = useConnectIntegration();
  const qc = useQueryClient();
  const disconnect = useDisconnectIntegration();
  const meta = SOCIAL_META[status.platform] ?? { label: status.platform, icon: "?", description: "" };

  async function handleConnect() {
    try {
      const { auth_url } = await connect.mutateAsync(status.platform);
      openOAuthPopup(auth_url, () => {
        void qc.invalidateQueries({ queryKey: settingsKeys.integrations() });
      });
    } catch {
      // surfaces via connect.isError
    }
  }

  return (
    <ConnectorRow>
      <ConnectorHeader
        icon={meta.icon}
        label={meta.label}
        description={status.connected && status.account_name ? `Connected as ${status.account_name}` : meta.description}
        rightSlot={
          <>
            {status.connected && !status.can_publish ? (
              <StatusBadge connected tone="amber" label="Action needed" />
            ) : (
              <StatusBadge connected={status.connected} />
            )}
            {status.connected ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => disconnect.mutate(status.platform)}
                disabled={disconnect.isPending}
              >
                {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={handleConnect} disabled={connect.isPending}>
                {connect.isPending ? "Connecting…" : "Connect"}
              </Button>
            )}
          </>
        }
      />
      {status.connected && status.warning && (
        <p className="mt-1 pl-12 text-xs text-amber-700">{status.warning}</p>
      )}
      {connect.isError && (
        <p className="mt-1 pl-12 text-xs text-destructive">
          {connect.error instanceof Error ? connect.error.message : "Connection failed"}
        </p>
      )}
    </ConnectorRow>
  );
}

// ─── Booking link ─────────────────────────────────────────────────────────────

function BookingLinkConnector() {
  const booking = useBookingLink();
  const update = useUpdateBookingLink();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (booking.data && !open) setUrl(booking.data.booking_url ?? "");
  }, [booking.data, open]);

  const configured = !!(booking.data?.booking_url);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      await update.mutateAsync({ booking_url: url.trim() || null });
      toast({ title: "Booking link saved", variant: "success" });
      setOpen(false);
    } catch (err) {
      toast({ title: "Could not save booking link", description: err instanceof Error ? err.message : "Unknown error", variant: "error" });
    }
  }

  async function handleClear() {
    try {
      await update.mutateAsync({ booking_url: null });
      setUrl("");
      toast({ title: "Booking link removed", variant: "success" });
      setOpen(false);
    } catch (err) {
      toast({ title: "Could not remove booking link", description: err instanceof Error ? err.message : "Unknown error", variant: "error" });
    }
  }

  return (
    <ConnectorRow>
      <ConnectorHeader
        icon="📅"
        label="Booking link"
        description="Your Calendly, Cal.com, or custom scheduling URL — auto-appended to SMS sequences."
        rightSlot={
          <>
            {booking.isLoading ? (
              <Skeleton className="h-5 w-24 rounded-full" />
            ) : (
              <StatusBadge connected={configured} label={configured ? "Set" : "Not set"} />
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
              {open ? "Cancel" : configured ? "Edit" : "Set URL"}
            </Button>
          </>
        }
      />

      {configured && !open && booking.data?.booking_url && (
        <div className="mt-2 pl-12">
          <a href={booking.data.booking_url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-primary hover:underline truncate block max-w-md">
            {booking.data.booking_url}
          </a>
        </div>
      )}

      {open && (
        <form onSubmit={handleSave} className="mt-4 pl-12 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="booking-url" className="text-xs">Scheduling URL</Label>
            <Input
              id="booking-url"
              type="url"
              placeholder="https://calendly.com/yourname/30min"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoComplete="off"
              className="h-8 text-sm max-w-md"
            />
            <p className="text-xs text-muted-foreground">Used in SMS templates via {"{{"}<code>booking_link</code>{"}}"}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={!url.trim() || update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            {configured && (
              <Button type="button" variant="outline" size="sm" onClick={handleClear} disabled={update.isPending} className="text-destructive hover:text-destructive">
                Remove
              </Button>
            )}
          </div>
        </form>
      )}
    </ConnectorRow>
  );
}

// ─── Coming soon ─────────────────────────────────────────────────────────────

function ComingSoonConnector({ icon, label, description }: { icon: string; label: string; description: string }) {
  return (
    <ConnectorRow>
      <div className="flex items-center gap-3 opacity-50">
        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600 select-none shrink-0">
          {icon}
        </div>
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-900">{label}</span>
          <span className="text-xs text-muted-foreground">{description}</span>
        </div>
        <span className="text-xs text-muted-foreground px-2.5 py-0.5 bg-gray-100 rounded-full shrink-0">Soon</span>
      </div>
    </ConnectorRow>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ConnectionsHomePage() {
  const integrations = useIntegrations();

  // Show connected=true banner when OAuth callback redirects back with ?connected=platform
  const connected = new URLSearchParams(window.location.search).get("connected");

  // When this page is the OAuth popup's landing (the provider redirected back here
  // with ?connected=…), close the popup so the opener — the real Relay tab — regains
  // focus and its poll refetches connection status (issue #118).
  useEffect(() => {
    if (connected && typeof window !== "undefined" && window.opener && window.opener !== window) {
      window.close();
    }
  }, [connected]);

  return (
    <div className="flex flex-col gap-8">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Module</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Connections</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Connect the tools your business already uses. Everything flows through here.
        </p>
      </header>

      {connected && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800 font-medium">
          {SOCIAL_META[connected]?.label ?? connected} connected successfully.
        </div>
      )}

      {/* Lead Channels */}
      <SectionCard
        title="Lead Channels"
        description="Where your leads come from — SMS, email, and web forms."
      >
        <TwilioConnector />
        <GmailConnector />
        <WebFormConnector />
      </SectionCard>

      {/* Social Publishing */}
      <SectionCard
        title="Social Publishing"
        description="Connect social accounts to publish content directly from the Content module."
      >
        {integrations.isLoading && (
          <ConnectorRow>
            <div className="flex flex-col gap-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-lg" />
              ))}
            </div>
          </ConnectorRow>
        )}
        {integrations.isError && (
          <ConnectorRow>
            <p className="text-sm text-destructive">Failed to load platform connections.</p>
          </ConnectorRow>
        )}
        {integrations.data?.integrations
          .filter(
            (status: SocialIntegrationStatus) =>
              INSTAGRAM_TILE_ENABLED || status.platform !== "instagram",
          )
          .map((status: SocialIntegrationStatus) => (
            <SocialConnector key={status.platform} status={status} />
          ))}
      </SectionCard>

      {/* Booking & Scheduling */}
      <SectionCard
        title="Scheduling"
        description="Set your booking link so leads can schedule directly from your SMS responses."
      >
        <BookingLinkConnector />
      </SectionCard>

      {/* Coming Soon */}
      <SectionCard
        title="Coming Soon"
        description="More connectors on the roadmap."
      >
        <ComingSoonConnector icon="S" label="Slack notifications" description="Get notified in Slack when a new lead comes in or a sequence completes." />
        <ComingSoonConnector icon="G" label="Google Business" description="Route Google Business reviews and messages into your lead pipeline." />
        <ComingSoonConnector icon="$" label="Stripe" description="Connect Stripe to track payments and close the lead-to-revenue loop." />
      </SectionCard>
    </div>
  );
}

export default ConnectionsHomePage;
